import { useState } from "react";
import {
  Button,
  CustomSelect,
  FormFieldSet,
  Modal,
} from "../../../../components";
import { DATE_RANGE_OPTIONS } from "../../../../assets/data/enumData";
import UploadModal from "./UploadModal";
import ActivityModal from "./ActivityModal";
import { getDatesByOption } from "../../../../utils/wf/date";
import selectOpionGenerator from "../../../../components/Table/TableAdv/tableUtils/selectOpionGenerator";
import { DEFAULT_SELECT_LIST, getPaginationRange } from "../../../../components/Table/TableAdv/TableSettings";

const fmtZ = (n) =>
  (n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function ClientBlock({ client, isLast }) {
  const { total } = client;

  return (
    <>
      <tr>
        <td
          colSpan={13}
          className="pt-2 pb-1.5 px-2 text-sm font-semibold text-gray-900 tracking-wide"
        >
          {client?.client}
        </td>
      </tr>

      {client?.invoices?.map((inv) => (
        <tr key={inv?.inv} className="hover:bg-gray-50 transition-colors">
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap"></td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-700">
            {inv?.inv}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-700">
            {inv?.date}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-700">
            {inv?.batch}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-800">
            {fmtZ(inv?.amount)}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-700">
            {inv?.paidDate}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-600">
            {inv?.age}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-800">
            {fmtZ(inv?.bal)}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-600">
            {fmtZ(inv?.d1_30)}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-600">
            {fmtZ(inv?.d31_60)}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-600">
            {fmtZ(inv?.d61_90)}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-600">
            {fmtZ(inv?.d91_120)}
          </td>
          <td className="px-2 py-1 text-sm text-center whitespace-nowrap text-gray-600">
            {fmtZ(inv?.d121)}
          </td>
        </tr>
      ))}

      {/* Subtotal */}
      <tr className={!isLast ? "border-b border-gray-400" : ""}>
        <td className="px-2 py-1 text-sm"></td>
        <td className="px-2 py-1 text-sm"></td>
        <td className="px-2 py-1 text-sm"></td>
        <td className="px-2 py-1 text-sm"></td>
        <td className="px-2 py-1 text-sm"></td>
        <td className="px-2 py-1 text-sm"></td>
        <td className="px-2 py-1 text-sm text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
          Total
        </td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {fmtZ(total?.bal)}
        </td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {fmtZ(total?.d1_30)}
        </td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {fmtZ(total?.d31_60)}
        </td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {fmtZ(total?.d61_90)}
        </td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {fmtZ(total?.d91_120)}
        </td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {fmtZ(total?.d121)}
        </td>
      </tr>

      <tr>
        <td colSpan={13} className="h-2"></td>
      </tr>
    </>
  );
}



export const UploadModalTable = ({ rows, columns }) => {
  const [data] = useState(rows);
  const DEFAULT_LIST = [10, 20, 30];
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(data?.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRecords = data?.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (val) => {
    setRowsPerPage(Number(val));
    setCurrentPage(1);
  };

  const toNum = (v) => {
    if (v == null || v === "") return 0;
    const cleaned = String(v)
      .replace(/,/g, "")
      .replace(/[^\d.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  };

  const grandTotal = currentRecords?.reduce(
    (acc, client) => ({
      bal: acc.bal + toNum(client?.total?.bal),
      d1_30: acc.d1_30 + toNum(client?.total?.d1_30),
      d31_60: acc.d31_60 + toNum(client?.total?.d31_60),
      d61_90: acc.d61_90 + toNum(client?.total?.d61_90),
      d91_120: acc.d91_120 + toNum(client?.total?.d91_120),
      d121: acc.d121 + toNum(client?.total?.d121),
    }),
    { bal: 0, d1_30: 0, d31_60: 0, d61_90: 0, d91_120: 0, d121: 0 },
  );

  const pages = getPaginationRange(currentPage, totalPages);

  return (
    <div className="bg-white min-h-full">
      <div className="py-2 px-2 flex items-center gap-2 text-sm flex-wrap">
        <span>
          {!data.length ? startIndex : startIndex + 1}–
          {Math.min(endIndex, data.length)}
        </span>
        <span className="text-nowrap">of {data.length}</span>

        <Button
          size="sm"
          type="icon"
          variant="text"
          color="black"
          icon="chevron_left"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          data-dropdown-persist="true"
        />

        <div className="flex items-center">
          {pages.map((page, index) =>
            page === "..." ? (
              <span key={`ellipsis-${index}`} className="px-0! text-gray-400">
                ...
              </span>
            ) : (
              <Button
                key={`page-${page}-${index}`}
                size="sm"
                variant={currentPage === page ? "contained" : "text"}
                color={currentPage === page ? "primary" : "black"}
                title={String(page)}
                className="min-w-[20px] !px-0 !py-0"
                onClick={() => handlePageChange(page)}
                data-dropdown-persist="true"
              />
            ),
          )}
        </div>

        <Button
          size="sm"
          icon="chevron_right"
          type="icon"
          variant="text"
          color="black"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          data-dropdown-persist="true"
        />

        <div className="w-32 lg:w-40">
          <CustomSelect
            size="sm"
            placeholder="Select rows"
            options={selectOpionGenerator(DEFAULT_LIST, rowsPerPage)}
            selected={String(rowsPerPage)}
            onChange={handleRowsPerPageChange}
            data-dropdown-persist="true"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead>
            <tr className="border-t-2 border-b-2 border-gray-900 bg-primary-50">
              {columns?.map(({ label }, i) => (
                <th
                  key={i}
                  className="px-2 py-5 text-xs font-bold whitespace-nowrap leading-tight align-bottom text-center"
                >
                  {label?.map((line, j) => (
                    <span key={j} className="block">
                      {line}
                    </span>
                  ))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRecords?.map((client, index) => (
              <ClientBlock
                key={client?.client}
                client={client}
                isLast={index === currentRecords.length - 1}
              />
            ))}

            <tr className="border-b-2 border-gray-900 bg-primary-50">
              <td className="px-2 py-1.5 text-sm"></td>
              <td colSpan={5}></td>
              <td className="px-2 py-1.5 text-sm font-bold text-center text-nowrap">
                Grand Total
              </td>
              <td className="px-2 py-1.5 text-sm font-bold text-center whitespace-nowrap text-primary-500">
                {fmtZ(grandTotal?.bal)}
              </td>
              <td className="px-2 py-1.5 text-sm font-bold text-center whitespace-nowrap text-primary-500">
                {fmtZ(grandTotal?.d1_30)}
              </td>
              <td className="px-2 py-1.5 text-sm font-bold text-center whitespace-nowrap text-primary-500">
                {fmtZ(grandTotal?.d31_60)}
              </td>
              <td className="px-2 py-1.5 text-sm font-bold text-center whitespace-nowrap text-primary-500">
                {fmtZ(grandTotal?.d61_90)}
              </td>
              <td className="px-2 py-1.5 text-sm font-bold text-center whitespace-nowrap text-primary-500">
                {fmtZ(grandTotal?.d91_120)}
              </td>
              <td className="px-2 py-1.5 text-sm font-bold text-center whitespace-nowrap text-primary-500">
                {fmtZ(grandTotal?.d121)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const AgingFilterBar = ({
  setActivityOpen,
  activityOpen,
  formData,
  handleChange,
  setAgingUpload,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const isCustomRange = formData?.reportPeriod === "custom";

  const handleDateRangeSelect = (val) => {
    handleChange("reportPeriod", val);

    if (val === "custom") {
      handleChange("fromDate", null);
      handleChange("toDate", null);
    } else {
      const range = getDatesByOption(val);
      if (range) {
        handleChange("fromDate", range.from);
        handleChange("toDate", range.to);
      }
    }
    setShowDropdown(false);
  };

  return (
    <>
      <div className="bg-white py-3 flex items-end gap-3 flex-wrap">
        {/* Date Range Select */}
        <FormFieldSet
          title="Report Period"
          placeholder="select Date Range"
          type="select"
          options={DATE_RANGE_OPTIONS}
          value={formData?.reportPeriod}
          onChange={handleDateRangeSelect}
          showDropdown={showDropdown}
        />

        {/* From - Only for Custom */}
        {isCustomRange && (
          <FormFieldSet
            title="From"
            type="datepicker"
            placeholder="select Date"
            value={formData?.fromDate}
            onChange={(val) => handleChange("fromDate", val)}
            showDropdown={false}
          />
        )}

        {/* To - Only for Custom */}
        {isCustomRange && (
          <FormFieldSet
            title="To"
            type="datepicker"
            placeholder="select Date"
            value={formData?.toDate}
            onChange={(val) => handleChange("toDate", val)}
            showDropdown={false}
            minDate={
              formData?.fromDate ? new Date(formData?.fromDate) : undefined
            }
          />
        )}

        <FormFieldSet
          noTitle
          type="text"
          placeholder="search"
          value={formData?.search}
          onChange={(val) => handleChange("search", val)}
        />

        <div className="flex-1" />

        <Button
          title={"Activity"}
          variant={"outlined"}
          onClick={() => setActivityOpen(true)}
        />
        <Button
          icon={"upload"}
          title={"Upload"}
          onClick={() => setAgingUpload(true)}
        />
      </div>

      <Modal
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        title="Activity"
        width="max-w-7xl"
        titleColor={"primary"}
        zIndex={55}
      >
        <ActivityModal
          isOpen={activityOpen}
          onClose={() => setActivityOpen(false)}
          type={"Aging Report"}
        />
      </Modal>
    </>
  );
};
